import { counted, search as fuzzySearch, } from '@debbie/common/dist/util/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import Check from './Icons/Check.js';
import Loading from './Loading.js';
import { ACCENT, Chevron, ChipCount, ChipIcon, Container, DropPanel, EmptyRow, FilterChip, FilterChips, List, NO_ROWS, Row, RowDescription, RowLabel, RowText, RowTile, SearchField, SearchIcon, SearchIconWrap, SearchRow, SectionHeader, Trigger, TriggerClear, TriggerClearSpace, TriggerIcon, TriggerLabel, TriggerMeta, nameCollator, storeRecent, useHighlightedRow, useRecentRows, useSearchableDropdown, } from './SearchableDropdown.js';
/**
 * Searchable select over one collection: alphabetical while browsing,
 * typo-tolerant search ranked by relevance, recently used items on top,
 * optional facet chips, keyboard navigation and an explicit clear button.
 * The entity selects adapt their collection to this through the accessors.
 */
const SearchableSelect = (props) => {
    const lang = useSelector((s) => s.lang);
    const { items: itemsLoadable, getId, getLabel, getSearchText = getLabel, getRecentsKey = getId, filter, browseFilter, facet, } = props;
    const items = h.hasData(itemsLoadable) ? itemsLoadable.data : null;
    const selectedIds = useMemo(() => new Set(props.multi === true
        ? props.value
        : props.value == null
            ? []
            : [props.value]), [props.multi, props.value]);
    const [facetKey, setFacetKey] = useState('all');
    const disabled = props.disabled === true || items == null;
    const { open, close, search, setSearch, containerProps, triggerProps } = useSearchableDropdown({
        disabled,
        autofocus: props.autofocus,
    });
    const { handleKeyDown, rowProps } = useHighlightedRow(open, JSON.stringify([search, facetKey]));
    if (!open && facetKey !== 'all') {
        setFacetKey('all');
    }
    // Nothing below is needed while the panel is closed, so the sort and the
    // facet counts wait for the first opening
    const eligibleItems = useMemo(() => {
        if (!open) {
            return NO_ROWS;
        }
        return (items || [])
            .filter((item) => (filter ? filter(item) : true))
            .toSorted((a, b) => nameCollator.compare(getLabel(a), getLabel(b)));
    }, [open, items, filter, getLabel]);
    const browsable = useMemo(() => (browseFilter ? eligibleItems.filter(browseFilter) : eligibleItems), [eligibleItems, browseFilter]);
    // While browsing the list stays alphabetical; a search returns typo-tolerant
    // matches ranked by relevance (best match first)
    const matches = useMemo(() => {
        const inFacet = facetKey === 'all' || facet == null
            ? browsable
            : eligibleItems.filter((item) => facet.of(item) === facetKey);
        if (search.trim() === '') {
            return inFacet;
        }
        return fuzzySearch(inFacet, getSearchText, search.trim(), {});
    }, [browsable, eligibleItems, facet, facetKey, search, getSearchText]);
    const { recentRows, rest, rows } = useRecentRows(props.recentsStorageKey, getId, getRecentsKey, open, search, matches);
    const extraRow = props.extraRow?.(search.trim()) ?? null;
    const optionCount = rows.length + (extraRow ? 1 : 0);
    const select = (item) => {
        if (disabled || props.isItemSelectable?.(item) === false) {
            return;
        }
        if (props.multi === true) {
            const id = getId(item);
            if (selectedIds.has(id)) {
                props.onChange(props.value.filter((value) => value !== id));
            }
            else {
                storeRecent(props.recentsStorageKey, getRecentsKey(item));
                props.onChange([...props.value, id]);
            }
            return;
        }
        storeRecent(props.recentsStorageKey, getRecentsKey(item));
        props.onChange(getId(item), item);
        close();
    };
    const selectExtraRow = () => {
        if (disabled) {
            return;
        }
        extraRow?.onSelect();
        close();
    };
    const selectedItem = useMemo(() => props.multi === true
        ? undefined
        : props.selectedItem !== undefined
            ? props.selectedItem
            : props.value != null
                ? (items || []).find((item) => getId(item) === props.value)
                : undefined, [props.multi, props.selectedItem, props.value, items, getId]);
    const selectedLabels = useMemo(() => {
        if (props.multi !== true) {
            return '';
        }
        const labelsById = new Map((items ?? []).map((item) => [getId(item), getLabel(item)]));
        return props.value.map((id) => labelsById.get(id) ?? id).join(', ');
    }, [props.multi, props.value, items, getId, getLabel]);
    const facetChips = useMemo(() => {
        if (facet == null || !open) {
            return [];
        }
        const counts = counted(eligibleItems, facet.of);
        const all = {
            key: 'all',
            label: lang.misc.all,
            count: browsable.length,
        };
        return [
            all,
            ...facet.options
                .map((option) => ({ ...option, count: counts[option.key] ?? 0 }))
                .filter((option) => facet.hideEmpty !== true || option.count > 0),
        ];
    }, [facet, open, eligibleItems, browsable, lang]);
    const renderRow = (item, index) => {
        const icon = props.getIcon?.(item);
        const description = props.getDescription?.(item);
        const actions = props.getRowActions?.(item);
        return (React.createElement(Row, { key: getId(item), ...rowProps(index), ...props.getRowAttributes?.(item), role: "option", "aria-disabled": props.isItemSelectable?.(item) === false || undefined, "aria-selected": selectedIds.has(getId(item)), $selected: selectedIds.has(getId(item)), onClick: () => select(item) },
            icon && (React.createElement(RowTile, { $palette: icon.palette, "aria-hidden": "true" }, icon.icon)),
            React.createElement(RowText, null,
                React.createElement(RowLabel, null, getLabel(item)),
                description != null && description !== '' && (React.createElement(RowDescription, null, description))),
            actions != null && (React.createElement(RowActions, { "aria-disabled": false, onClick: (e) => e.stopPropagation() }, actions)),
            props.multi === true && selectedIds.has(getId(item)) && (React.createElement(Check, { "aria-hidden": "true" }))));
    };
    const selectedIcon = selectedItem != null ? props.getIcon?.(selectedItem) : null;
    const clearable = selectedIds.size > 0 && !disabled;
    return (React.createElement(Container, { ...containerProps, style: props.style, className: props.className },
        React.createElement(TriggerControl, null,
            React.createElement(Trigger, { ...triggerProps },
                props.busy === true || h.isLoading(itemsLoadable) ? (React.createElement(Loading, { width: "16px", style: { margin: '-2px 0' } })) : (selectedIcon && (React.createElement(TriggerIcon, { $palette: selectedIcon.palette, "aria-hidden": "true" }, selectedIcon.icon))),
                React.createElement(TriggerLabel, { $placeholder: selectedIds.size === 0, title: selectedItem != null ? getLabel(selectedItem) : selectedLabels }, props.multi === true
                    ? selectedLabels || props.placeholder
                    : selectedItem != null
                        ? getLabel(selectedItem)
                        : items != null && props.value != null
                            ? props.value
                            : props.placeholder),
                props.multi === true && selectedIds.size > 0 && (React.createElement(TriggerMeta, null, selectedIds.size)),
                selectedItem != null && props.getTriggerMeta && (React.createElement(TriggerMeta, null, props.getTriggerMeta(selectedItem))),
                clearable && React.createElement(TriggerClearSpace, null),
                React.createElement(Chevron, { $open: open, "aria-hidden": "true" }, "\u25BE")),
            clearable && (React.createElement(TriggerClear, { label: lang.inputs.clear, onClear: () => {
                    close();
                    if (props.multi === true) {
                        props.onChange([]);
                    }
                    else {
                        props.onChange(null, null);
                    }
                } }))),
        open && (React.createElement(DropPanel, null,
            React.createElement(SearchRow, null,
                React.createElement(SearchIconWrap, null,
                    React.createElement(SearchIcon, null)),
                React.createElement(SearchField, { autoFocus: true, value: search, placeholder: props.labels.search, onChange: (e) => setSearch(e.target.value), onKeyDown: (e) => handleKeyDown(e, optionCount, (index) => {
                        if (index < rows.length) {
                            select(rows[index]);
                        }
                        else {
                            selectExtraRow();
                        }
                    }) })),
            facetChips.length > 0 && (React.createElement(FilterChips, null, facetChips.map((chip) => (React.createElement(FilterChip, { key: chip.key, type: "button", $active: facetKey === chip.key, onClick: () => setFacetKey(chip.key) },
                chip.icon && (React.createElement(ChipIcon, { "aria-hidden": "true" }, chip.icon)),
                chip.label,
                React.createElement(ChipCount, null, chip.count)))))),
            React.createElement(List, { role: "listbox", "aria-multiselectable": props.multi === true || undefined }, optionCount === 0 ? (React.createElement(EmptyRow, null, lang.inputs.noResults)) : (React.createElement(React.Fragment, null,
                recentRows.length > 0 && (React.createElement(React.Fragment, null,
                    React.createElement(SectionHeader, null, lang.inputs.recentlyUsed),
                    recentRows.map((item, index) => renderRow(item, index)),
                    rest.length > 0 && (React.createElement(SectionHeader, null, props.labels.all)))),
                rest.map((item, index) => renderRow(item, recentRows.length + index)),
                extraRow && (React.createElement(Row, { ...rowProps(rows.length), role: "option", onClick: selectExtraRow },
                    React.createElement(ExtraTile, { "aria-hidden": "true" }, extraRow.icon),
                    React.createElement(RowText, null,
                        React.createElement(RowLabel, null, extraRow.label),
                        extraRow.description != null && (React.createElement(RowDescription, null, extraRow.description))))))))))));
};
export default SearchableSelect;
const TriggerControl = styled.div `
  position: relative;
`;
const RowActions = styled.span `
  flex: none;
  display: inline-flex;
  gap: 2px;
`;
const ExtraTile = styled.span `
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1.5px dashed ${ACCENT};
  color: ${ACCENT};

  & svg {
    width: 13px;
    height: 13px;
  }
`;
//# sourceMappingURL=SearchableSelect.js.map